import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { AppShell } from "@/lovable-demo/components/app-shell";
import { KpiCard, Panel, StatusChip } from "@/lovable-demo/components/bits";
import { Input } from "@/components/ui/input";
import {
  fmtCompactCurrency,
  fmtCurrency,
  fmtDate,
  workflowProgress,
} from "@/lovable-demo/lib/warranty/calc";
import {
  dateIsPast,
  openRecordSummary,
  openRecordWork,
} from "@/lovable-demo/lib/warranty/open-records";
import { useWarranty } from "@/lovable-demo/lib/warranty/store";

export const Route = createFileRoute("/demo/open-problem-records")({
  head: () => ({
    meta: [
      { title: "Open Problem Records — Broadview Intelligence" },
      {
        name: "description",
        content:
          "Open Broadview Problem Records across all programs, with outstanding workflow stages, responsible owners, due dates and overdue work.",
      },
      { property: "og:title", content: "Open Problem Records — Broadview Intelligence" },
      {
        property: "og:description",
        content:
          "Review outstanding workflow stages, responsible owners and dates across open Problem Records, all programs combined.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: OpenProblemRecords,
});

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

function OpenProblemRecords() {
  const { data } = useWarranty();
  const [scope, setScope] = useState("all");
  const [query, setQuery] = useState("");
  const [owner, setOwner] = useState("all");
  const [stageIndex, setStageIndex] = useState("all");
  const [today, setToday] = useState("");
  useEffect(() => {
    setToday(new Date().toISOString().slice(0, 10));
  }, []);

  const scopeProgram = data.programs.find((p) => p.id === scope);
  const scoped = scopeProgram
    ? data.records.filter((r) => r.programId === scopeProgram.id)
    : data.records;
  const summaryPrograms = scopeProgram ? [scopeProgram] : data.programs;
  const work = openRecordWork(scoped);
  const summary = openRecordSummary(scoped, summaryPrograms, today);
  const owners = [
    ...new Set(work.flatMap(({ record, stages }) => [record.owner, ...stages.map((s) => s.owner)])),
  ].sort();
  const stageNames = new Map(
    work.flatMap(({ stages }) => stages.map((s) => [s.index, s.name] as const)),
  );
  const rows = work.flatMap(({ record, stages }) => {
    const q = query.trim().toLowerCase();
    if (
      q &&
      ![record.code, record.title, record.owner, ...stages.flatMap((s) => [s.name, s.owner])]
        .join(" ")
        .toLowerCase()
        .includes(q)
    )
      return [];
    const matching = stages.filter(
      (s) =>
        (stageIndex === "all" || String(s.index) === stageIndex) &&
        (owner === "all" || s.owner === owner || record.owner === owner),
    );
    if (
      (stageIndex !== "all" || owner !== "all") &&
      matching.length === 0 &&
      !(owner === record.owner && stageIndex === "all")
    )
      return [];
    return [{ record, stages: matching }];
  });
  const outstanding = rows.flatMap((r) => r.stages);
  const maxStageGroup = Math.max(1, ...summary.byStage.map((b) => b.stages));
  const maxOwnerGroup = Math.max(1, ...summary.byOwner.map((b) => b.stages));
  const scopeLabel = scopeProgram
    ? `${scopeProgram.code} · ${scopeProgram.name} · MY${scopeProgram.modelYear}`
    : `All programs combined · ${data.programs.length} programs`;

  return (
    <AppShell title="Open Problem Records" subtitle={scopeLabel}>
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          label="Open records"
          value={String(summary.records)}
          delta={`${summary.critical} critical`}
          hint={
            scopeProgram ? `${scopeProgram.code} only` : `${data.programs.length} programs combined`
          }
          tone="warn"
        />
        <KpiCard
          label="Outstanding workflow stages"
          value={String(summary.outstanding)}
          delta={`${summary.inProgress} in progress`}
          hint={`${summary.notStarted} not yet started`}
        />
        <KpiCard
          label="Overdue stages"
          value={today ? String(summary.overdue) : "—"}
          delta={
            today && summary.recordsWithOverdue
              ? `${summary.recordsWithOverdue} records affected`
              : undefined
          }
          hint={today ? `As of ${fmtDate(today)}` : undefined}
          tone="warn"
        />
        <KpiCard
          label="Remaining exposure"
          value={fmtCompactCurrency(summary.remaining)}
          delta={`${fmtCompactCurrency(summary.delay)}/mo delay cost`}
          hint="Open records only · total exposure minus actual cost to date"
          tone="good"
        />
      </div>

      <div className="mt-4 grid gap-4 xl:grid-cols-3">
        <Panel
          title="Open workload by program"
          subtitle={scopeProgram ? "Selected program" : "Every program, combined at the total row"}
          className="xl:col-span-2"
        >
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="border-b border-border uppercase tracking-wider text-muted-foreground">
                <tr>
                  {[
                    "Program",
                    "Open",
                    "Critical",
                    "Stages open",
                    "Overdue",
                    "Remaining exposure",
                    "Delay/mo",
                  ].map((label) => (
                    <th
                      key={label}
                      className={
                        label === "Program"
                          ? "px-2 py-2 text-left font-medium"
                          : "px-2 py-2 text-right font-medium"
                      }
                    >
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border/60">
                {summary.byProgram.map((p) => (
                  <tr key={p.programId}>
                    <td className="px-2 py-2.5">
                      <span className="num block text-primary">{p.code}</span>
                      <span className="block max-w-[170px] truncate">{p.name}</span>
                      <span className="block text-muted-foreground">MY{p.modelYear}</span>
                    </td>
                    <td className="num whitespace-nowrap px-2 py-2.5 text-right">{p.open}</td>
                    <td className="num whitespace-nowrap px-2 py-2.5 text-right">
                      {p.critical || <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="num whitespace-nowrap px-2 py-2.5 text-right">
                      {p.outstanding}
                    </td>
                    <td
                      className={`num whitespace-nowrap px-2 py-2.5 text-right ${p.overdue ? "text-warning" : "text-muted-foreground"}`}
                    >
                      {today ? p.overdue || "—" : "—"}
                    </td>
                    <td className="num whitespace-nowrap px-2 py-2.5 text-right text-success">
                      {fmtCompactCurrency(p.remaining)}
                    </td>
                    <td className="num whitespace-nowrap px-2 py-2.5 text-right text-warning">
                      {fmtCompactCurrency(p.delay)}
                    </td>
                  </tr>
                ))}
                {summary.byProgram.length === 0 && (
                  <tr>
                    <td colSpan={7} className="py-8 text-center text-muted-foreground">
                      No open records in this scope.
                    </td>
                  </tr>
                )}
              </tbody>
              <tfoot className="border-t-2 border-border">
                <tr className="font-semibold">
                  <td className="px-2 py-2.5">
                    {scopeProgram ? "Selected program" : "All programs"}
                  </td>
                  <td className="num px-2 py-2.5 text-right">{summary.records}</td>
                  <td className="num px-2 py-2.5 text-right">{summary.critical || "—"}</td>
                  <td className="num px-2 py-2.5 text-right">{summary.outstanding}</td>
                  <td className="num whitespace-nowrap px-2 py-2.5 text-right text-warning">
                    {today ? summary.overdue : "—"}
                  </td>
                  <td className="num whitespace-nowrap px-2 py-2.5 text-right text-success">
                    {fmtCompactCurrency(summary.remaining)}
                  </td>
                  <td className="num whitespace-nowrap px-2 py-2.5 text-right text-warning">
                    {fmtCompactCurrency(summary.delay)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </Panel>

        <Panel
          title="Where open records sit"
          subtitle="Record stage of governance, and the dates that matter"
        >
          <dl className="space-y-3 text-sm">
            {[
              ["Records past target close", today ? String(summary.pastTarget) : "—"],
              ["Records with overdue work", today ? String(summary.recordsWithOverdue) : "—"],
              [
                "Oldest record opened",
                summary.oldestOpenedAt ? fmtDate(summary.oldestOpenedAt) : "—",
              ],
              ["Actual cost to date", fmtCurrency(summary.actual)],
              ["Total exposure", fmtCurrency(summary.exposure)],
              ["Remaining exposure", fmtCurrency(summary.remaining)],
            ].map(([k, v]) => (
              <div
                key={k}
                className="flex items-baseline justify-between gap-3 border-b border-border/60 pb-2 last:border-0"
              >
                <dt className="text-muted-foreground">{k}</dt>
                <dd className="num font-semibold">{v}</dd>
              </div>
            ))}
          </dl>
          <ul className="mt-3 space-y-1.5">
            {summary.byStatus.map((b) => (
              <li key={b.key} className="flex items-baseline justify-between gap-3 text-sm">
                <span className="text-muted-foreground">{b.label}</span>
                <span className="num">{plural(b.records, "record")}</span>
              </li>
            ))}
          </ul>
        </Panel>
      </div>

      <div className="mt-4 grid gap-4 xl:grid-cols-2">
        <Panel
          title="Outstanding work by workflow stage"
          subtitle="How many open records are sitting at each stage"
        >
          <ul className="space-y-2.5">
            {summary.byStage.map((b) => (
              <li key={b.key}>
                <div className="flex items-baseline justify-between gap-3 text-sm">
                  <span className="truncate">{b.label}</span>
                  <span className="num shrink-0 text-xs text-muted-foreground">
                    {plural(b.stages, "stage")}
                  </span>
                </div>
                <div className="mt-1 h-1.5 overflow-hidden rounded bg-elevated">
                  <div
                    className={`h-1.5 rounded ${b.overdue > 0 ? "bg-warning/70" : "bg-primary/60"}`}
                    style={{
                      width: `${Math.max(4, Math.round((b.stages / maxStageGroup) * 100))}%`,
                    }}
                  />
                </div>
                <p className="mt-1 text-[0.68rem] text-muted-foreground">
                  {plural(b.records, "record")}
                  {today && b.overdue > 0 && (
                    <span className="text-warning"> · {plural(b.overdue, "stage")} overdue</span>
                  )}
                </p>
              </li>
            ))}
            {summary.byStage.length === 0 && (
              <li className="py-6 text-center text-sm text-muted-foreground">
                No outstanding workflow stages.
              </li>
            )}
          </ul>
        </Panel>

        <Panel
          title="Outstanding work by responsible owner"
          subtitle="Who is holding the open stages across the portfolio"
        >
          <ul className="space-y-2.5">
            {summary.byOwner.map((b) => (
              <li key={b.key || "unassigned"}>
                <div className="flex items-baseline justify-between gap-3 text-sm">
                  <span className="truncate">{b.label}</span>
                  <span className="num shrink-0 text-xs text-muted-foreground">
                    {plural(b.stages, "stage")}
                  </span>
                </div>
                <div className="mt-1 h-1.5 overflow-hidden rounded bg-elevated">
                  <div
                    className={`h-1.5 rounded ${b.overdue > 0 ? "bg-warning/70" : "bg-primary/60"}`}
                    style={{
                      width: `${Math.max(4, Math.round((b.stages / maxOwnerGroup) * 100))}%`,
                    }}
                  />
                </div>
                <p className="mt-1 text-[0.68rem] text-muted-foreground">
                  {plural(b.records, "record")}
                  {today && b.overdue > 0 && (
                    <span className="text-warning"> · {plural(b.overdue, "stage")} overdue</span>
                  )}
                </p>
              </li>
            ))}
            {summary.byOwner.length === 0 && (
              <li className="py-6 text-center text-sm text-muted-foreground">
                No outstanding workflow stages.
              </li>
            )}
          </ul>
        </Panel>
      </div>

      <div className="my-5 flex flex-wrap items-center gap-3">
        <select
          aria-label="Program scope"
          value={scope}
          onChange={(e) => setScope(e.target.value)}
          className="h-9 max-w-full rounded border border-input bg-elevated px-2 text-sm"
        >
          <option value="all">All programs combined</option>
          {data.programs.map((p) => (
            <option key={p.id} value={p.id}>
              {p.code} · {p.name}
            </option>
          ))}
        </select>
        <Input
          aria-label="Search open records"
          placeholder="Search record, stage or owner…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="max-w-sm bg-elevated"
        />
        <select
          aria-label="Responsible owner"
          value={owner}
          onChange={(e) => setOwner(e.target.value)}
          className="h-9 max-w-full rounded border border-input bg-elevated px-2 text-sm"
        >
          <option value="all">All responsible owners</option>
          {owners.map((name) => (
            <option key={name} value={name}>
              {name || "Unassigned"}
            </option>
          ))}
        </select>
        <select
          aria-label="Workflow stage"
          value={stageIndex}
          onChange={(e) => setStageIndex(e.target.value)}
          className="h-9 max-w-full rounded border border-input bg-elevated px-2 text-sm"
        >
          <option value="all">All outstanding stages</option>
          {[...stageNames]
            .sort(([a], [b]) => a - b)
            .map(([index, name]) => (
              <option key={index} value={index}>
                Stage {index} · {name}
              </option>
            ))}
        </select>
        <span className="text-xs text-muted-foreground">
          {rows.length === work.length
            ? `${plural(work.length, "open record")} in this scope`
            : `${rows.length} of ${plural(work.length, "open record")}`}
        </span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[950px] text-left text-sm">
          <thead className="border-y border-border text-xs uppercase text-muted-foreground">
            <tr>
              {[
                "Problem Record / owner",
                "Program",
                "Opened",
                "Target close",
                "Progress",
                "Outstanding workflow / responsible owner / due date",
              ].map((label) => (
                <th key={label} className="px-3 py-3 font-medium">
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map(({ record, stages }) => {
              const progress = workflowProgress(record.stages);
              const program = data.programs.find((p) => p.id === record.programId);
              return (
                <tr key={record.id} className="align-top">
                  <td className="w-64 px-3 py-4">
                    <Link
                      to="/demo/problem-record/$id"
                      params={{ id: record.id }}
                      className="font-medium hover:text-primary"
                    >
                      <span className="num block text-xs text-primary">{record.code}</span>
                      <span className="mt-1 block">{record.title}</span>
                    </Link>
                    <p className="my-2 text-xs text-muted-foreground">
                      Record owner · {record.owner || "Unassigned"}
                    </p>
                    <StatusChip status={record.status} />
                  </td>
                  <td className="px-3 py-4 text-xs">
                    <span className="num block text-primary">{program?.code ?? "—"}</span>
                    <span className="block max-w-[140px] truncate text-muted-foreground">
                      {program?.name ?? "Unassigned program"}
                    </span>
                  </td>
                  <td className="num whitespace-nowrap px-3 py-4 text-xs">
                    {fmtDate(record.openedAt)}
                  </td>
                  <td className="num whitespace-nowrap px-3 py-4 text-xs">
                    {fmtDate(record.targetCloseAt)}
                    {today && dateIsPast(record.targetCloseAt, today) && (
                      <span className="mt-1 block text-warning">Overdue</span>
                    )}
                  </td>
                  <td className="num px-3 py-4 text-xs">
                    {progress.done}/{progress.total}
                  </td>
                  <td className="px-3 py-4">
                    {stages.length === 0 ? (
                      <p className="text-muted-foreground">
                        All stages complete · record remains open
                      </p>
                    ) : (
                      <ul className="space-y-3">
                        {stages.map((s) => (
                          <li
                            key={s.id}
                            className="grid grid-cols-[minmax(150px,1fr)_120px] gap-x-4 gap-y-1 border-b border-border/50 pb-2 last:border-0"
                          >
                            <span className="font-medium">
                              Stage {s.index} · {s.name}
                            </span>
                            <span className="num text-right text-xs">{fmtDate(s.dueDate)}</span>
                            <span className="text-xs text-muted-foreground">
                              {s.owner || "Unassigned"} ·{" "}
                              {s.status === "in-progress" ? "In progress" : "Not started"}
                            </span>
                            <span className="text-right text-xs text-warning">
                              {today && dateIsPast(s.dueDate, today) ? "Overdue" : ""}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr>
                <td colSpan={6} className="py-10 text-center text-muted-foreground">
                  No open records match the current filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </AppShell>
  );
}
